import type { ChartDataPoint, EChartsSeriesData } from '../types/chart.types';
import { DATA_TYPE_CONFIG } from '@/features/dashboard/constants/dataTypes';
import type { DataTypeKey } from '@/features/dashboard/types/dashboard.types';
import { DateFactory } from '@/shared/utils/dateFactory';

// ========== データタイプマッピング ==========
const DATA_TYPE_MAPPING: Record<string, keyof ChartDataPoint> = {
  'l_af_supply_volume': 'lAfSupplyVolume',
  'l_af_supply_volume_integral': 'lAfSupplyVolumeIntegral',
  'h_af_supply_volume': 'hAfSupplyVolume',
  'h_af_supply_volume_integral': 'hAfSupplyVolumeIntegral',
  'concrete_supply_volume': 'concreteSupplyVolume',
  'concrete_supply_volume_integral': 'concreteSupplyVolumeIntegral',
  'l_h_ratio': 'lHRatio',
  'l_af_concrete_ratio': 'lAfConcreteRatio',
  'h_af_concrete_ratio': 'hAfConcreteRatio',
};

const ONE_MINUTE_MS = 60 * 1000;
const TWO_MINUTES_MS = 2 * 60 * 1000; // 2分のミリ秒（線を切断する閾値）

/**
 * チャートデータ変換ユーティリティ
 */
export class ChartDataTransformer {
  /**
   * APIレスポンスからEChartsシリーズデータに変換
   * @param dataPoints チャートデータポイント配列
   * @param selectedDataTypes 選択されたデータタイプ
   * @returns EChartsシリーズデータ配列と元のデータポイント
   */
  static transformToEChartsSeries(params: {
    dataPoints: ChartDataPoint[];
    selectedDataTypes: string[];
  }): { series: EChartsSeriesData[]; originalPoints: ChartDataPoint[] } {
    const { selectedDataTypes } = params;
    // Deep copyしてから処理
    const dataPointsCopy: ChartDataPoint[] = structuredClone(params.dataPoints);

    // タイムスタンプをDateオブジェクトに復元（API応答が文字列の場合の防御）
    dataPointsCopy.forEach((point) => {
      if (typeof point.timestamp === 'string') {
        point.timestamp = DateFactory.fromISOString(point.timestamp);
      }
    });

    // 1分間隔でデータを間引く
    const downsampledDataPoints = this.downsampleByMinute(dataPointsCopy);

    const seriesArray: EChartsSeriesData[] = [];

    selectedDataTypes.forEach((dataTypeKey) => {
      const fieldName = DATA_TYPE_MAPPING[dataTypeKey];
      if (!fieldName) {
        return;
      }

      const config = DATA_TYPE_CONFIG[dataTypeKey as DataTypeKey];
      if (!config) {
        return;
      }

      // データポイントを [timestamp, value] 形式に変換（ギャップにnullを挿入）
      const seriesData: Array<[Date, number] | [Date, null]> = [];

      for (let i = 0; i < downsampledDataPoints.length; i++) {
        const point = downsampledDataPoints[i];
        const value = point[fieldName];

        // 前のポイントとの時間差をチェック
        if (i > 0) {
          const prevPoint = downsampledDataPoints[i - 1];
          const timeDiff = point.timestamp.getTime() - prevPoint.timestamp.getTime();

          // 2分以上のギャップがある場合はnullを挿入して線を切断
          if (timeDiff > TWO_MINUTES_MS) {
            // ギャップの中間地点にnullを挿入
            const middleTime = DateFactory.fromTimestamp(prevPoint.timestamp.getTime() + timeDiff / 2);
            seriesData.push([middleTime, null]);
          }
        }

        // 値がnullまたはundefinedの場合もnullとして扱う
        if (value === null || value === undefined) {
          seriesData.push([point.timestamp, null]);
        } else {
          seriesData.push([point.timestamp, value] as [Date, number]);
        }
      }

      const series: EChartsSeriesData = {
        name: config.label,
        type: 'line',
        data: seriesData,
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        unit: config.unit,
        precision: config.precision,
        lineStyle: {
          width: 1.5,
        },
        itemStyle: {
          color: config.color,
        },
        connectNulls: false,  // nullの位置で線を切断
      };

      seriesArray.push(series);
    });

    return {
      series: seriesArray,
      originalPoints: downsampledDataPoints
    };
  }

  /**
   * データポイントを1分間隔で間引く
   * @param dataPoints チャートデータポイント配列
   * @returns 1分間隔に間引かれたデータポイント配列
   */
  private static downsampleByMinute(dataPoints: ChartDataPoint[]): ChartDataPoint[] {
    if (dataPoints.length === 0) {
      return [];
    }

    const result: ChartDataPoint[] = [];
    let lastMinuteTimestamp = 0;

    dataPoints.forEach((point) => {
      const currentMinuteTimestamp = Math.floor(point.timestamp.getTime() / ONE_MINUTE_MS) * ONE_MINUTE_MS;

      // 1分間隔でデータを選択
      if (currentMinuteTimestamp !== lastMinuteTimestamp) {
        result.push(point);
        lastMinuteTimestamp = currentMinuteTimestamp;
      }
    });

    return result;
  }

  /**
   * データタイプキーからフィールド名を取得
   * @param dataTypeKey データタイプキー
   * @returns フィールド名
   */
  static getFieldName(dataTypeKey: string): keyof ChartDataPoint | undefined {
    return DATA_TYPE_MAPPING[dataTypeKey];
  }

  /**
   * 選択されたデータタイプのラベル一覧を取得
   * @param selectedDataTypes 選択されたデータタイプ
   * @returns ラベル配列
   */
  static getDataTypeLabels(selectedDataTypes: string[]): string[] {
    const labels: string[] = [];
    selectedDataTypes.forEach((dataTypeKey) => {
      const config = DATA_TYPE_CONFIG[dataTypeKey as DataTypeKey];
      if (config) {
        labels.push(config.label);
      }
    });
    return labels;
  }

  /**
   * データポイントが空かチェック
   * @param dataPoints チャートデータポイント配列
   * @returns 空の場合true
   */
  static isEmpty(dataPoints: ChartDataPoint[]): boolean {
    return dataPoints.length === 0;
  }

  /**
   * データポイントの時刻範囲を取得
   * @param dataPoints チャートデータポイント配列
   * @returns 開始時刻と終了時刻
   */
  static getTimeRange(dataPoints: ChartDataPoint[]): {
    startTime: Date | null;
    endTime: Date | null;
  } {
    if (dataPoints.length === 0) {
      return { startTime: null, endTime: null };
    }

    // Deep copy
    const dataPointsCopy: ChartDataPoint[] = structuredClone(dataPoints);

    // タイムスタンプをDateオブジェクトに復元（API応答が文字列の場合の防御）
    dataPointsCopy.forEach((point) => {
      if (typeof point.timestamp === 'string') {
        point.timestamp = DateFactory.fromISOString(point.timestamp);
      }
    });

    const timestamps = dataPointsCopy.map((point) => point.timestamp.getTime());
    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);

    return {
      startTime: DateFactory.fromTimestamp(minTime),
      endTime: DateFactory.fromTimestamp(maxTime),
    };
  }
}
